import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAlert } from "../../contexts/AlertContext";
import {
    Box,
    Typography,
    Divider,
    Chip,
    Stack,
    TextField,
    IconButton,
    Button,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Collapse,
    Stepper,
    Step,
    StepLabel,
    Checkbox,
    LinearProgress,
    Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RouteIcon from "@mui/icons-material/Route";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import PrintIcon from "@mui/icons-material/Print";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { documentsApi, DocumentApiResponse, DocumentProduct, DocumentPzWzAllocationsResponse } from "../../services/documentsApi";
import { getStorageLocations, StorageLocationOption, resolveLocationQR, openPdfBlobInBrowser, suggestLocations } from "../../services/locationApi";
import { barcodesApi } from "../../services/barcodesApi";
import { ProductBarcodeResponse } from "../../services/productsApi";
import { useTranslation } from "react-i18next";
import CustomBox from "../CustomBox/CustomBox";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
} from "@mui/material";
import { PickingPathView } from "../WarehouseMap";
import { BarcodeScanner } from "../BarcodeScanner";
import { PzWzAllocationsPanel } from "../PzWzAllocations/PzWzAllocationsPanel";

const textFieldStyles = {
    "& .MuiOutlinedInput-root": {
        "& fieldset": { borderColor: "#474747ff" },
        "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" },
        "&.Mui-focused fieldset": { borderColor: "primary.main" },
        "&.Mui-disabled fieldset": { borderColor: "#474747ff" },
        "&.Mui-disabled:hover fieldset": { borderColor: "#474747ff" },
    },
    "& .MuiInputLabel-root": { color: "text.secondary" },
    "& .MuiInputLabel-root.Mui-focused": { color: "text.secondary" },
    "& .MuiInputLabel-root.Mui-disabled": { color: "rgba(255, 255, 255, 0.7)" },
    "& .MuiInputBase-input": { color: "text.primary" },
    "& .MuiInputBase-input.Mui-disabled": {
        color: "#ffffff",
        WebkitTextFillColor: "#ffffff",
        opacity: 1,
    },
    "& .MuiOutlinedInput-root.Mui-disabled": {
        "& .MuiInputBase-input": {
            color: "#ffffff",
            WebkitTextFillColor: "#ffffff",
        },
    },
    "& .hide-number-spinners": {
        "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
        },
        "&[type=number]": {
            MozAppearance: "textfield",
        },
    },
};

/** Format date from API (YYYY-MM-DD) to display format DD.MM.YYYY */
const formatDateDisplay = (dateStr: string | null | undefined): string => {
    if (!dateStr) return "";
    const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[3]}.${match[2]}.${match[1]}`;
    return dateStr;
};

const LabeledTextField: React.FC<{
    label: string;
    value: any;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    placeholder?: string;
    type?: string;
    endAdornment?: React.ReactNode;
    disabled?: boolean;
}> = ({ label, value, onChange, placeholder, type, endAdornment, disabled }) => (
    <TextField
        fullWidth
        label={label}
        value={value ?? ""}
        onChange={onChange}
        variant="outlined"
        size="small"
        placeholder={placeholder}
        type={type}
        sx={textFieldStyles}
        autoComplete="off"
        disabled={disabled}
        InputProps={{
            endAdornment,
            inputProps: {
                inputMode: type === "number" ? "numeric" : undefined,
                className: type === "number" ? "hide-number-spinners" : "",
            },
        }}
        InputLabelProps={type === "date" ? { shrink: true } : undefined}
    />
);

const STATUS_LABELS: Record<string, string> = {
    oczekujaca: "Oczekująca",
    zweryfikowano: "Zweryfikowano",
    nowy: "Nowy",
    zrealizowany: "Zrealizowany",
    w_realizacji: "W trakcie realizacji",
    wyslana: "Wysłana",
    dostarczona: "Dostarczona",
    utworzony: "Utworzony",
    zatwierdzony: "Zatwierdzony",
    anulowany: "Anulowany",
};

const getStatusColor = (status: string): "success" | "error" | "warning" | "info" | "default" => {
    switch (status) {
        case "zweryfikowano":
        case "zrealizowany":
        case "dostarczona":
        case "zatwierdzony":
            return "success";
        case "anulowany":
            return "error";
        case "w_realizacji":
        case "wyslana":
            return "info";
        case "oczekujaca":
        case "nowy":
        case "utworzony":
            return "warning";
        default:
            return "default";
    }
};

export const DocumentDetails: React.FC = () => {
    const { t } = useTranslation();
    const { showAlert } = useAlert();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [document, setDocument] = useState<DocumentApiResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [statusChangeDialogOpen, setStatusChangeDialogOpen] = useState(false);
    const [newStatus, setNewStatus] = useState<string>("");
    const [pzDialogOpen, setPzDialogOpen] = useState(false);
    const [pzQuantities, setPzQuantities] = useState<Record<number, number>>({});
    const [pzLocations, setPzLocations] = useState<Record<number, Array<{locationId: number | null, quantity: number}>>>({});
    const [storageLocations, setStorageLocations] = useState<StorageLocationOption[]>([]);
    const [isCreatingPZ, setIsCreatingPZ] = useState(false);
    const [printingKT, setPrintingKT] = useState(false);
    const [allocations, setAllocations] = useState<DocumentPzWzAllocationsResponse | null>(null);
    const [allocationsLoading, setAllocationsLoading] = useState(false);

    // PZ Barcode Step state
    const [pzStep, setPzStep] = useState(0);
    const [pzProductBarcodes, setPzProductBarcodes] = useState<Record<number, ProductBarcodeResponse[]>>({});
    type PzBarcodeStatus = "has_primary" | "scanned" | "generated" | "skipped" | "pending";
    const [pzBarcodeStatuses, setPzBarcodeStatuses] = useState<Record<number, PzBarcodeStatus>>({});
    const [pzScannerOpen, setPzScannerOpen] = useState(false);
    const [pzScanningForProductId, setPzScanningForProductId] = useState<number | null>(null);
    const [pzBarcodeLoading, setPzBarcodeLoading] = useState<number | null>(null);

    // PZ Location QR scan state
    const [pzLocationScannerOpen, setPzLocationScannerOpen] = useState(false);
    const [pzLocationScanProductId, setPzLocationScanProductId] = useState<number | null>(null);

    // WZ Verification state
    const [wzVerificationExpanded, setWzVerificationExpanded] = useState(false);
    const [wzVerifiedProducts, setWzVerifiedProducts] = useState<Set<number>>(new Set());
    const [wzScannerOpen, setWzScannerOpen] = useState(false);

    useEffect(() => {
        const fetchDocument = async () => {
            if (!id) {
                setError("Brak ID dokumentu");
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                setError(null);
                const documentId = parseInt(id, 10);
                if (isNaN(documentId)) {
                    throw new Error("Nieprawidłowe ID dokumentu");
                }
                const fetchedDocument = await documentsApi.getDocument(documentId);
                setDocument(fetchedDocument);
                setNewStatus(fetchedDocument.document_status);
            } catch (err: any) {
                const errorMessage =
                    err.response?.data?.detail ||
                    err.response?.data?.error ||
                    err.message ||
                    "Błąd podczas pobierania dokumentu";
                setError(errorMessage);
                showAlert("error", errorMessage, 5000);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDocument();
    }, [id, showAlert]);

    const allocationsDocId = document?.id;
    const allocationsDocType = document?.document_type;
    const allocationsDocStatus = document?.document_status;

    useEffect(() => {
        if (!id || !allocationsDocId) return;
        if (!allocationsDocType || !["PZ", "WZ", "KOR"].includes(allocationsDocType)) {
            setAllocations(null);
            return;
        }
        const documentId = parseInt(id, 10);
        if (isNaN(documentId)) return;
        let cancelled = false;
        const fetchAllocations = async () => {
            try {
                setAllocationsLoading(true);
                const data = await documentsApi.getPzWzAllocations(documentId);
                if (!cancelled) setAllocations(data);
            } catch {
                if (!cancelled) setAllocations(null);
            } finally {
                if (!cancelled) setAllocationsLoading(false);
            }
        };
        fetchAllocations();
        return () => {
            cancelled = true;
        };
    }, [id, allocationsDocId, allocationsDocType, allocationsDocStatus]);

    const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!document) return;

        let value: any = e.target.value;

        if (["location_id", "client_id"].includes(field)) {
            value = value ? parseInt(value) : undefined;
        }

        setDocument((prev) => (prev ? { ...prev, [field]: value } : null));
    };

    const handleSave = async () => {
        if (!document) return;

        try {
            setIsSaving(true);
            const updatedDocument = await documentsApi.updateDocument(document.id, {
                document_date: document.document_date,
                location_id: document.location,
                client_id: document.client,
                document_notes: document.document_notes,
            });
            setDocument(updatedDocument);
            showAlert("success", t("documents.messages.updated") || "Dokument zaktualizowany");
        } catch (err: any) {
            const errorMessage =
                err.response?.data?.detail ||
                err.response?.data?.error ||
                err.message ||
                "Błąd podczas zapisywania dokumentu";
            showAlert("error", errorMessage, 5000);
        } finally {
            setIsSaving(false);
        }
    };

    const handleStatusChange = async () => {
        if (!document || !newStatus) return;

        try {
            setIsSaving(true);
            const updatedDocument = await documentsApi.changeStatus(document.id, newStatus);
            setDocument(updatedDocument);
            setNewStatus(updatedDocument.document_status);
            setStatusChangeDialogOpen(false);
            showAlert("success", t("documents.messages.statusChanged") || "Status dokumentu został zmieniony");
        } catch (err: any) {
            const errorMessage =
                err.response?.data?.detail ||
                err.response?.data?.error ||
                err.message ||
                "Błąd podczas zmiany statusu dokumentu";
            showAlert("error", errorMessage, 5000);
        } finally {
            setIsSaving(false);
        }
    };

    const handleOpenPZDialog = async () => {
        if (!document?.products || !document.location) return;
        const initialQuantities: Record<number, number> = {};
        const initialLocations: Record<number, Array<{locationId: number | null, quantity: number}>> = {};
        document.products.forEach((p) => {
            initialQuantities[p.product_id] = p.quantity;
            initialLocations[p.product_id] = [{ locationId: null, quantity: p.quantity }];
        });
        setPzQuantities(initialQuantities);
        setPzLocations(initialLocations);
        try {
            const locs = await getStorageLocations(document.location);
            setStorageLocations(locs);
        } catch {
            setStorageLocations([]);
        }
        setPzDialogOpen(true);
    };

    // Fetch barcodes for all products when entering step 2
    const fetchPzBarcodes = useCallback(async () => {
        if (!document?.products) return;
        const barcodeMap: Record<number, ProductBarcodeResponse[]> = {};
        const statusMap: Record<number, PzBarcodeStatus> = {};
        for (const p of document.products) {
            try {
                const barcodes = await barcodesApi.getProductBarcodes(p.product_id);
                barcodeMap[p.product_id] = barcodes;
                const hasPrimary = barcodes.some((b) => b.is_primary);
                statusMap[p.product_id] = hasPrimary ? "has_primary" : barcodes.length > 0 ? "has_primary" : "pending";
            } catch {
                barcodeMap[p.product_id] = [];
                statusMap[p.product_id] = "pending";
            }
        }
        setPzProductBarcodes(barcodeMap);
        setPzBarcodeStatuses(statusMap);
    }, [document?.products]);

    const handlePzNextStep = async () => {
        if (pzStep === 0) {
            setPzStep(1);
            await fetchPzBarcodes();
        }
    };

    const handlePzBackStep = () => {
        if (pzStep === 1) setPzStep(0);
    };

    const handlePzScanLocationQR = (productId: number) => {
        setPzLocationScanProductId(productId);
        setPzLocationScannerOpen(true);
    };

    const handlePzLocationScanResult = async (code: string) => {
        setPzLocationScannerOpen(false);
        if (pzLocationScanProductId === null) return;
        try {
            const resolved = await resolveLocationQR(code);
            const match = storageLocations.find(loc => loc.id === resolved.id);
            if (match) {
                setPzLocations(prev => {
                    const splits = prev[pzLocationScanProductId] ?? [{ locationId: null, quantity: 0 }];
                    const firstEmpty = splits.findIndex(s => s.locationId === null);
                    const idx = firstEmpty >= 0 ? firstEmpty : 0;
                    const updated = [...splits];
                    updated[idx] = { ...updated[idx], locationId: resolved.id };
                    return { ...prev, [pzLocationScanProductId]: updated };
                });
                showAlert("success", `Lokalizacja: ${resolved.rackCode ? resolved.rackCode + ' / ' : ''}${resolved.code}`);
            } else {
                showAlert("warning", `Lokalizacja ${resolved.code} nie należy do tego magazynu`);
            }
        } catch {
            showAlert("error", "Nie rozpoznano kodu QR lokalizacji");
        }
        setPzLocationScanProductId(null);
    };

    const handlePzScanBarcode = (productId: number) => {
        setPzScanningForProductId(productId);
        setPzScannerOpen(true);
    };

    const handlePzScanResult = async (code: string) => {
        setPzScannerOpen(false);
        if (pzScanningForProductId === null) return;
        const productId = pzScanningForProductId;
        setPzBarcodeLoading(productId);
        try {
            const bc = await barcodesApi.addBarcode(productId, {
                code,
                is_primary: true,
                source: "manufacturer",
            });
            setPzProductBarcodes((prev) => ({
                ...prev,
                [productId]: [...(prev[productId] || []), bc],
            }));
            setPzBarcodeStatuses((prev) => ({ ...prev, [productId]: "scanned" }));
        } catch {
            // ignore duplicate barcode errors, still mark as scanned
            setPzBarcodeStatuses((prev) => ({ ...prev, [productId]: "scanned" }));
        } finally {
            setPzBarcodeLoading(null);
            setPzScanningForProductId(null);
        }
    };

    const handlePzGenerateBarcode = async (productId: number) => {
        setPzBarcodeLoading(productId);
        try {
            const bc = await barcodesApi.generateBarcode(productId);
            setPzProductBarcodes((prev) => ({
                ...prev,
                [productId]: [...(prev[productId] || []), bc],
            }));
            setPzBarcodeStatuses((prev) => ({ ...prev, [productId]: "generated" }));
        } catch {
            // ignore
        } finally {
            setPzBarcodeLoading(null);
        }
    };

    const handlePzSkipBarcode = (productId: number) => {
        setPzBarcodeStatuses((prev) => ({ ...prev, [productId]: "skipped" }));
    };

    const handlePzPrintLabel = async (productId: number) => {
        const barcodes = pzProductBarcodes[productId] || [];
        const primary = barcodes.find((b) => b.is_primary) || barcodes[0];
        if (primary) {
            try {
                await barcodesApi.downloadLabel(primary.id, "pdf");
            } catch {
                // download failed silently
            }
        }
    };

    const pzBarcodeStepComplete = document?.products?.every(
        (p) => pzBarcodeStatuses[p.product_id] && pzBarcodeStatuses[p.product_id] !== "pending"
    ) ?? false;

    // WZ Verification handlers
    const handleWzScanResult = (code: string) => {
        setWzScannerOpen(false);
        if (!document?.products) return;

        const verifyProduct = (productId: number) => {
            const match = document.products?.find(
                (p) => p.product_id === productId || p.id === productId
            );
            if (match) {
                setWzVerifiedProducts((prev) => {
                    const next = new Set(Array.from(prev));
                    next.add(match.product_id);
                    return next;
                });
                showAlert("success", t("barcodes.scanSuccess", "Zeskanowano!"));
            } else {
                showAlert("warning", t("documents.wz.productNotInDocument", "Ten produkt nie jest w tym dokumencie"));
            }
        };

        const localMatch = document.products?.find((p) => p.product_code === code);
        if (localMatch) {
            verifyProduct(localMatch.product_id);
            return;
        }

        barcodesApi.barcodeLookup(code).then((result) => {
            verifyProduct(result.product.id);
        }).catch(() => {
            showAlert("error", t("barcodes.notFound", "Nie znaleziono produktu dla tego kodu"));
        });
    };

    const handleWzManualVerify = (productId: number) => {
        setWzVerifiedProducts((prev) => {
            const next = new Set(prev);
            if (next.has(productId)) {
                next.delete(productId);
            } else {
                next.add(productId);
            }
            return next;
        });
    };

    const wzTotalProducts = document?.products?.length ?? 0;
    const wzVerifiedCount = wzVerifiedProducts.size;
    const wzProgress = wzTotalProducts > 0 ? (wzVerifiedCount / wzTotalProducts) * 100 : 0;
    const isWzInProgress = document?.document_type === "WZ" && document?.document_status === "w_realizacji";

    const handleCreatePZ = async () => {
        if (!document) return;

        try {
            setIsCreatingPZ(true);
            const products: Array<{product_id: number; received_quantity: number; storage_location_id?: number}> = [];
            Object.entries(pzQuantities).forEach(([productId, totalQty]) => {
                const pid = parseInt(productId, 10);
                const splits = pzLocations[pid];
                if (splits && splits.length > 1) {
                    splits.forEach((split) => {
                        if (split.quantity > 0) {
                            products.push({
                                product_id: pid,
                                received_quantity: split.quantity,
                                ...(split.locationId != null ? { storage_location_id: split.locationId } : {}),
                            });
                        }
                    });
                } else {
                    const locationId = splits?.[0]?.locationId;
                    products.push({
                        product_id: pid,
                        received_quantity: totalQty,
                        ...(locationId != null ? { storage_location_id: locationId } : {}),
                    });
                }
            });

            const result = await documentsApi.createPZFromAwizacja(document.id, { products });

            let message = `Utworzono PZ: ${result.pz.document_number}`;
            if (result.kor) {
                message += ` oraz Korektę: ${result.kor.document_number}`;
            }
            showAlert("success", message);
            setPzDialogOpen(false);
            navigate(`/documents/${result.pz.id}`);
        } catch (err: any) {
            const errorMessage =
                err.response?.data?.detail ||
                err.response?.data?.error ||
                err.message ||
                "Błąd podczas tworzenia PZ";
            showAlert("error", errorMessage, 5000);
        } finally {
            setIsCreatingPZ(false);
        }
    };

    const handlePrintKT = async () => {
        if (!document) return;
        setPrintingKT(true);

        // Must be opened synchronously to avoid popup blockers.
        const previewWindow = window.open("", "_blank");
        try {
            const blobUrl = await documentsApi.printKT(document.id);
            openPdfBlobInBrowser(previewWindow, blobUrl);
        } catch (err: any) {
            const message =
                err?.response?.data?.error ||
                err?.response?.data?.detail ||
                err?.message ||
                (t("documents.printKTError") || "Failed to generate KT PDF");
            showAlert("error", message, 5000);
        } finally {
            setPrintingKT(false);
        }
    };

    const [pickingPathExpanded, setPickingPathExpanded] = useState(false);

    const canEdit = document?.document_status ? !['zrealizowany', 'dostarczona', 'anulowany', 'zweryfikowano', 'zatwierdzony'].includes(document.document_status) : false;
    const canChangeStatus = document && (document.allowed_transitions?.length ?? 0) > 0;
    const canCreatePZ = document?.document_type === 'A' && document?.document_status === 'oczekujaca';
    const isReleaseDocument = document?.document_type === "WZ" || document?.document_type === "release";
    const canShowPickingPath = isReleaseDocument && document?.products && document.products.length > 0 && document.location;
    const canPrintKT =
        (document?.document_type === "WZ" || document?.document_type === "release") &&
        (document?.products?.length ?? 0) > 0 &&
        ["w_realizacji", "wyslana", "dostarczona"].includes(document?.document_status);
    const isPackingDocument = document?.document_type === "PA";
    const packedProducts = isPackingDocument ? (document?.products?.slice(0, -1) ?? []) : [];
    const packingResultProducts = isPackingDocument
        ? ((document?.products?.length ?? 0) > 1 ? (document?.products?.slice(-1) ?? []) : (document?.products ?? []))
        : [];

    /** Stałe proporcje kolumn dla dwóch tabel PA (komponenty + wynik) — identyczne szerokości niezależnie od długości nazwy. */
    const packingProductsTableColgroup = (
        <colgroup>
            <col style={{ width: "42%" }} />
            <col style={{ width: "18%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "15%" }} />
            <col style={{ width: "15%" }} />
        </colgroup>
    );
    const packingNameCellSx = {
        color: "white",
        borderColor: "#474747ff",
        minWidth: 0,
        wordBreak: "break-word" as const,
        overflowWrap: "break-word" as const,
    };
    const packingCodeCellSx = {
        color: "white",
        borderColor: "#474747ff",
        minWidth: 0,
        wordBreak: "break-word" as const,
    };

    const renderProductsTable = (products: DocumentProduct[], opts?: { fixedPackingLayout?: boolean }) => {
        const fixedPacking = opts?.fixedPackingLayout === true;
        return (
            <TableContainer component={Paper} sx={{ backgroundColor: "transparent", mt: 2 }}>
                <Table
                    sx={
                        fixedPacking
                            ? { tableLayout: "fixed", width: "100%" }
                            : undefined
                    }
                >
                    {fixedPacking ? packingProductsTableColgroup : null}
                    <TableHead>
                        <TableRow>
                            <TableCell
                                sx={
                                    fixedPacking
                                        ? packingNameCellSx
                                        : { color: "white", borderColor: "#474747ff" }
                                }
                            >
                                {t("documents.columns.productName") || "Nazwa produktu"}
                            </TableCell>
                            <TableCell
                                sx={
                                    fixedPacking
                                        ? packingCodeCellSx
                                        : { color: "white", borderColor: "#474747ff" }
                                }
                            >
                                {t("documents.columns.productCode") || "Kod produktu"}
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{ color: "white", borderColor: "#474747ff" }}
                            >
                                {t("documents.columns.quantity") || "Ilość"}
                            </TableCell>
                            {document?.document_type === 'PZ' && (
                                <TableCell sx={{ color: "white", borderColor: "#474747ff" }}>
                                    {t("documents.columns.storageLocation") || "Lokalizacja"}
                                </TableCell>
                            )}
                            {document?.document_type === 'PP' && (
                                <TableCell sx={{ color: "white", borderColor: "#474747ff" }}>
                                    {t("documents.columns.sourceStorageLocation", { defaultValue: "Skąd" })}
                                </TableCell>
                            )}
                            {document?.document_type === 'PP' && (
                                <TableCell sx={{ color: "white", borderColor: "#474747ff" }}>
                                    {t("documents.columns.targetStorageLocation", { defaultValue: "Dokąd" })}
                                </TableCell>
                            )}
                            <TableCell align="right" sx={{ color: "white", borderColor: "#474747ff" }}>
                                {t("documents.columns.unitPrice") || "Cena jednostkowa"}
                            </TableCell>
                            <TableCell align="right" sx={{ color: "white", borderColor: "#474747ff" }}>
                                {t("documents.columns.total") || "Razem"}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {products.map((product) => (
                            <TableRow key={product.id}>
                                <TableCell
                                    sx={
                                        fixedPacking
                                            ? packingNameCellSx
                                            : { color: "white", borderColor: "#474747ff" }
                                    }
                                >
                                    {product.product_name}
                                </TableCell>
                                <TableCell
                                    sx={
                                        fixedPacking
                                            ? packingCodeCellSx
                                            : { color: "white", borderColor: "#474747ff" }
                                    }
                                >
                                    {product.product_code || "-"}
                                </TableCell>
                                <TableCell align="right" sx={{ color: "white", borderColor: "#474747ff" }}>
                                    {product.quantity}
                                </TableCell>
                                {document?.document_type === 'PZ' && (
                                    <TableCell sx={{ color: "white", borderColor: "#474747ff" }}>
                                        {product.storage_location_code || "-"}
                                    </TableCell>
                                )}
                                {document?.document_type === 'PP' && (
                                    <TableCell sx={{ color: "white", borderColor: "#474747ff", fontFamily: "monospace" }}>
                                        {product.source_storage_location_code || product.source_storage_location_name || "-"}
                                    </TableCell>
                                )}
                                {document?.document_type === 'PP' && (
                                    <TableCell sx={{ color: "white", borderColor: "#474747ff", fontFamily: "monospace" }}>
                                        {product.storage_location_code || product.storage_location_name || "-"}
                                    </TableCell>
                                )}
                                <TableCell align="right" sx={{ color: "white", borderColor: "#474747ff" }}>
                                    {parseFloat(product.unit_price).toFixed(2)} {document?.document_currency}
                                </TableCell>
                                <TableCell align="right" sx={{ color: "white", borderColor: "#474747ff" }}>
                                    {(product.quantity * parseFloat(product.unit_price)).toFixed(2)} {document?.document_currency}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    };

    if (isLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error || !document) {
        return (
            <Box>
                <Stack direction="row" alignItems="center" mb={2}>
                    <IconButton
                        onClick={() => navigate("/documents")}
                        sx={{
                            color: "white",
                            "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                        }}
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography color="white" variant="h6" fontWeight={500}>
                        {t("documents.details.details")}
                    </Typography>
                </Stack>
                <CustomBox>
                    <Typography color="white" variant="h6">
                        {error || "Dokument nie został znaleziony"}
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={() => navigate("/documents")}
                        sx={{ mt: 2 }}
                    >
                        {t("common.back") || "Powrót"}
                    </Button>
                </CustomBox>
            </Box>
        );
    }

    return (
        <Box>
            <Stack direction="row" alignItems="center" mb={2}>
                <IconButton
                    onClick={() => navigate("/documents")}
                    sx={{
                        color: "white",
                        "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography color="white" variant="h6" fontWeight={500}>
                    {t("documents.details.details") || "Szczegóły dokumentu"}
                </Typography>
            </Stack>

            <CustomBox>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="h5" gutterBottom color="white">
                            {document.document_number}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" mt={1}>
                            <Chip
                                label={document.document_type_display}
                                color="primary"
                                size="small"
                                variant="outlined"
                                sx={{
                                    borderColor: "#474747ff",
                                    color: "white",
                                    "& .MuiChip-label": { color: "white" },
                                }}
                            />
                            <Chip
                                label={STATUS_LABELS[document.document_status] || document.document_status_display}
                                color={getStatusColor(document.document_status)}
                                size="small"
                                variant="outlined"
                                sx={{
                                    color: "white",
                                    "& .MuiChip-label": { color: "white" },
                                }}
                            />
                        </Stack>
                    </Box>

                    <Divider />

                    <Stack spacing={2}>
                        <Typography variant="h6" color="textSecondary">
                            {t("documents.details.basicInfo") || "Podstawowe informacje"}
                        </Typography>
                        <Stack spacing={1} direction={{ xs: "column", md: "row" }}>
                            <Stack flex={1}>
                                <LabeledTextField
                                    label={t("documents.form.documentNumber") || "Numer dokumentu"}
                                    value={document.document_number}
                                    onChange={() => { }}
                                    disabled={true}
                                />
                            </Stack>
                            <Stack flex={1}>
                                <LabeledTextField
                                    label={t("documents.form.documentType") || "Typ dokumentu"}
                                    value={document.document_type_display}
                                    onChange={() => { }}
                                    disabled={true}
                                />
                            </Stack>
                        </Stack>
                        <Stack spacing={1} direction={{ xs: "column", md: "row" }}>
                            <Stack flex={1}>
                                <LabeledTextField
                                    label={t("documents.form.documentDate") || "Data dokumentu"}
                                    value={document.document_date}
                                    onChange={handleChange("document_date")}
                                    type="date"
                                    disabled={!canEdit}
                                />
                            </Stack>
                            <Stack flex={1}>
                                <LabeledTextField
                                    label={t("documents.columns.amount") || "Kwota"}
                                    value={`${parseFloat(document.document_amount || "0").toFixed(2)} ${document.document_currency}`}
                                    onChange={() => { }}
                                    disabled={true}
                                />
                            </Stack>
                        </Stack>
                        {document.document_type === 'A' && document.delivery_date && (
                            <Stack flex={1}>
                                <LabeledTextField
                                    label={t("documents.form.deliveryDate") || "Data dostawy"}
                                    value={formatDateDisplay(document.delivery_date)}
                                    onChange={() => { }}
                                    disabled={true}
                                />
                            </Stack>
                        )}
                        {document.location_name && (
                            <Stack flex={1}>
                                <LabeledTextField
                                    label={t("documents.form.location") || "Lokalizacja"}
                                    value={document.location_name}
                                    onChange={() => { }}
                                    disabled={true}
                                />
                            </Stack>
                        )}
                        {document.client_name && (
                            <Stack flex={1}>
                                <LabeledTextField
                                    label={t("documents.form.client") || "Klient"}
                                    value={document.client_name}
                                    onChange={() => { }}
                                    disabled={true}
                                />
                            </Stack>
                        )}
                        {document.supplier_name && (
                            <Stack flex={1}>
                                <LabeledTextField
                                    label={t("documents.form.supplier") || "Dostawca"}
                                    value={document.supplier_name}
                                    onChange={() => { }}
                                    disabled={true}
                                />
                            </Stack>
                        )}
                        {document.source_document_number && (
                            <Stack flex={1}>
                                <LabeledTextField
                                    label={t("documents.details.sourceDocument") || "Dokument źródłowy"}
                                    value={document.source_document_number}
                                    onChange={() => { }}
                                    disabled={true}
                                />
                            </Stack>
                        )}
                    </Stack>

                    {document.products && document.products.length > 0 && (
                        <>
                            <Divider />
                            <Box>
                                <Typography variant="h6" color="textSecondary" gutterBottom>
                                    {t("documents.details.products") || "Produkty w dokumencie"}
                                </Typography>
                                {document.document_type === 'PP' && (
                                    <Alert
                                        severity="info"
                                        sx={{
                                            mt: 1.5,
                                            mb: 1.5,
                                            bgcolor: "rgba(25, 118, 210, 0.12)",
                                            color: "#fff",
                                            "& .MuiAlert-message": { color: "#fff" },
                                            "& .MuiAlert-icon": { color: "#fff" },
                                        }}
                                    >
                                        {t("documents.details.ppFlowInfo", {
                                            defaultValue:
                                                "PP realizuje jawny ruch magazynowy: z lokalizacji źródłowej (skąd) do lokalizacji docelowej (dokąd) dla każdej pozycji.",
                                        })}
                                    </Alert>
                                )}
                                {isPackingDocument ? (
                                    <>
                                        {packedProducts.length > 0 && (
                                            <>
                                                <Typography variant="subtitle1" color="textSecondary" sx={{ mt: 2 }}>
                                                    {t("documents.details.packingInputProducts") || "Produkty pakowane"}
                                                </Typography>
                                                {renderProductsTable(packedProducts, { fixedPackingLayout: true })}
                                                <Divider sx={{ mt: 2 }} />
                                            </>
                                        )}
                                        <Typography variant="subtitle1" color="textSecondary" sx={{ mt: 2 }}>
                                            {t("documents.details.packingOutputProducts") || "Produkt wynikowy pakowania"}
                                        </Typography>
                                        {renderProductsTable(packingResultProducts, { fixedPackingLayout: true })}
                                    </>
                                ) : (
                                    renderProductsTable(document.products)
                                )}
                            </Box>
                        </>
                    )}

                    {document && ["PZ", "WZ", "KOR"].includes(document.document_type) && (
                        <>
                            <Divider />
                            <Box>
                                <Typography variant="h6" color="textSecondary" gutterBottom>
                                    {allocations?.view === "source" || document.document_type === "PZ"
                                        ? t("documents.details.issuesFromReceipt")
                                        : t("documents.details.sourceReceipts")}
                                </Typography>
                                <PzWzAllocationsPanel
                                    receipts={allocations?.receipts}
                                    outgoingLines={allocations?.outgoing_lines}
                                    loading={allocationsLoading}
                                    showProductColumn={(document.products?.length || 0) > 1}
                                />
                            </Box>
                        </>
                    )}

                    {/* Picking Path Section for release documents */}
                    {canShowPickingPath && (
                        <>
                            <Divider />
                            <Box>
                                <Button
                                    onClick={() => setPickingPathExpanded(!pickingPathExpanded)}
                                    startIcon={<RouteIcon />}
                                    endIcon={pickingPathExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    variant="outlined"
                                    fullWidth
                                    sx={{
                                        justifyContent: "space-between",
                                        color: "white",
                                        borderColor: "#474747ff",
                                        py: 1.5,
                                        "&:hover": {
                                            borderColor: "primary.main",
                                            backgroundColor: "rgba(25, 118, 210, 0.1)",
                                        },
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography>
                                            {t("documents.pickingPath.title")}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={t("documents.pickingPath.productsCount", { count: document.products.length })}
                                            sx={{ backgroundColor: "primary.main", color: "white" }}
                                        />
                                    </Box>
                                </Button>

                                <Collapse in={pickingPathExpanded}>
                                    <Box
                                        sx={{
                                            mt: 2,
                                            p: 2,
                                            backgroundColor: "rgba(0, 0, 0, 0.2)",
                                            borderRadius: 2,
                                            border: "1px solid #474747ff",
                                        }}
                                    >
                                        <PickingPathView
                                            warehouseId={document.location!}
                                            documentId={document.id}
                                            items={document.products.map(p => ({
                                                productId: p.product_id || p.id,
                                                quantity: p.quantity,
                                                productName: p.product_name,
                                                productCode: p.product_code || '',
                                            }))}
                                        />
                                    </Box>
                                </Collapse>
                            </Box>
                        </>
                    )}

                    {/* WZ Verification Section */}
                    {isWzInProgress && document.products && document.products.length > 0 && (
                        <>
                            <Divider />
                            <Box>
                                <Button
                                    onClick={() => setWzVerificationExpanded(!wzVerificationExpanded)}
                                    startIcon={<QrCodeScannerIcon />}
                                    endIcon={wzVerificationExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    variant="outlined"
                                    fullWidth
                                    sx={{
                                        justifyContent: "space-between",
                                        color: "white",
                                        borderColor: "#474747ff",
                                        py: 1.5,
                                        "&:hover": {
                                            borderColor: "primary.main",
                                            backgroundColor: "rgba(0, 169, 140, 0.1)",
                                        },
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <Typography>
                                            {t("documents.wz.verify", "Weryfikacja produktów")}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={`${wzVerifiedCount}/${wzTotalProducts}`}
                                            sx={{
                                                backgroundColor: wzVerifiedCount === wzTotalProducts ? "success.main" : "primary.main",
                                                color: "white",
                                            }}
                                        />
                                    </Box>
                                </Button>

                                <Collapse in={wzVerificationExpanded}>
                                    <Box
                                        sx={{
                                            mt: 2,
                                            p: 2,
                                            backgroundColor: "rgba(0, 0, 0, 0.2)",
                                            borderRadius: 2,
                                            border: "1px solid #474747ff",
                                        }}
                                    >
                                        <Stack spacing={2}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography variant="body2" color="text.secondary">
                                                    {t("documents.wz.verifyProgress", "Postęp weryfikacji")}: {wzVerifiedCount}/{wzTotalProducts}
                                                </Typography>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<QrCodeScannerIcon />}
                                                    onClick={() => setWzScannerOpen(true)}
                                                    size="small"
                                                >
                                                    {t("documents.wz.scanToVerify", "Skanuj kod")}
                                                </Button>
                                            </Stack>

                                            <LinearProgress
                                                variant="determinate"
                                                value={wzProgress}
                                                sx={{
                                                    height: 8,
                                                    borderRadius: 4,
                                                    bgcolor: "rgba(255,255,255,0.1)",
                                                    "& .MuiLinearProgress-bar": {
                                                        borderRadius: 4,
                                                        bgcolor: wzVerifiedCount === wzTotalProducts ? "success.main" : "primary.main",
                                                    },
                                                }}
                                            />

                                            <TableContainer component={Paper} sx={{ backgroundColor: "transparent" }}>
                                                <Table size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell padding="checkbox" sx={{ borderColor: "#474747ff" }} />
                                                            <TableCell sx={{ color: "white", borderColor: "#474747ff" }}>
                                                                {t("documents.columns.productName", "Produkt")}
                                                            </TableCell>
                                                            <TableCell sx={{ color: "white", borderColor: "#474747ff" }}>
                                                                {t("documents.columns.productCode", "Kod")}
                                                            </TableCell>
                                                            <TableCell align="right" sx={{ color: "white", borderColor: "#474747ff" }}>
                                                                {t("documents.columns.quantity", "Ilość")}
                                                            </TableCell>
                                                            <TableCell align="center" sx={{ color: "white", borderColor: "#474747ff" }}>
                                                                {t("documents.wz.status", "Status")}
                                                            </TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {document.products.map((p) => {
                                                            const verified = wzVerifiedProducts.has(p.product_id);
                                                            return (
                                                                <TableRow
                                                                    key={p.product_id}
                                                                    sx={{
                                                                        bgcolor: verified ? "rgba(0, 169, 140, 0.08)" : "transparent",
                                                                        transition: "background-color 0.3s",
                                                                    }}
                                                                >
                                                                    <TableCell padding="checkbox" sx={{ borderColor: "#474747ff" }}>
                                                                        <Checkbox
                                                                            checked={verified}
                                                                            onChange={() => handleWzManualVerify(p.product_id)}
                                                                            sx={{ color: "text.secondary", "&.Mui-checked": { color: "primary.main" } }}
                                                                        />
                                                                    </TableCell>
                                                                    <TableCell sx={{ color: "white", borderColor: "#474747ff" }}>
                                                                        {p.product_name}
                                                                    </TableCell>
                                                                    <TableCell sx={{ color: "text.secondary", borderColor: "#474747ff", fontFamily: "monospace" }}>
                                                                        {p.product_code || "-"}
                                                                    </TableCell>
                                                                    <TableCell align="right" sx={{ color: "white", borderColor: "#474747ff" }}>
                                                                        {p.quantity}
                                                                    </TableCell>
                                                                    <TableCell align="center" sx={{ borderColor: "#474747ff" }}>
                                                                        {verified ? (
                                                                            <CheckCircleIcon sx={{ color: "primary.main" }} fontSize="small" />
                                                                        ) : (
                                                                            <Typography variant="caption" color="text.secondary">—</Typography>
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                            );
                                                        })}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Stack>
                                    </Box>
                                </Collapse>
                            </Box>

                            <BarcodeScanner
                                open={wzScannerOpen}
                                onScan={handleWzScanResult}
                                onClose={() => setWzScannerOpen(false)}
                                title={t("documents.wz.scanToVerify", "Skanuj kod do weryfikacji")}
                            />
                        </>
                    )}

                    <Divider />
                    <TextField
                        fullWidth
                        label={t("documents.form.notes") || "Uwagi"}
                        value={document.document_notes || ""}
                        onChange={handleChange("document_notes")}
                        variant="outlined"
                        multiline
                        rows={4}
                        sx={textFieldStyles}
                        autoComplete="off"
                        disabled={!canEdit}
                    />

                    <Divider />

                    <Box>
                        <Typography variant="h6" color="textSecondary" gutterBottom>
                            {t("documents.details.metadata") || "Informacje dodatkowe"}
                        </Typography>
                        <Stack spacing={1} mt={1}>
                            <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
                                {t("documents.details.createdBy") || "Utworzony przez"}: {document.created_by_name} ({new Date(document.document_created_at).toLocaleString()})
                            </Typography>
                            <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
                                {t("documents.details.updatedBy") || "Zaktualizowany przez"}: {document.updated_by_name} ({new Date(document.document_updated_at).toLocaleString()})
                            </Typography>
                        </Stack>
                    </Box>

                    <Box
                        sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2, flexWrap: "wrap" }}
                    >
                        {canCreatePZ && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={handleOpenPZDialog}
                                disabled={isSaving}
                                sx={{ minWidth: 160,
                                    "&:hover": { backgroundColor: "primary.dark" },
                                 }}
                            >
                                {t("documents.createPZ") || "Utwórz PZ"}
                            </Button>
                        )}
                        {canChangeStatus && (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setStatusChangeDialogOpen(true)}
                                disabled={isSaving}
                                sx={{
                                    minWidth: 120,
                                    "&:hover": { backgroundColor: "primary.dark" },
                                }}
                            >
                                {t("documents.changeStatus") || "Zmień status"}
                            </Button>
                        )}
                        {canPrintKT && (
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<PrintIcon />}
                                onClick={handlePrintKT}
                                disabled={isSaving || printingKT}
                                sx={{
                                    minWidth: 160,
                                    borderColor: "primary.main",
                                    "&:hover": {
                                        borderColor: "primary.dark",
                                        backgroundColor: "rgba(0, 169, 140, 0.08)",
                                    },
                                }}
                            >
                                {printingKT ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    t("documents.printKT") || "Drukuj KT"
                                )}
                            </Button>
                        )}
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => navigate("/documents")}
                            disabled={isSaving}
                            sx={{
                                minWidth: 120,
                                "&:hover": { backgroundColor: "primary.dark" },
                            }}
                        >
                            {t("common.cancel") || "Anuluj"}
                        </Button>
                        {canEdit && (
                            <Button
                                variant="contained"
                                color="primary"
                                sx={{
                                    minWidth: 120,
                                    "&:hover": { backgroundColor: "primary.dark" },
                                }}
                                onClick={handleSave}
                                disabled={isSaving}
                            >
                                {isSaving ? (
                                    <CircularProgress size={24} color="inherit" />
                                ) : (
                                    t("common.saveChanges") || "Zapisz zmiany"
                                )}
                            </Button>
                        )}
                    </Box>
                </Stack>
            </CustomBox>

            {/* Status Change Dialog */}
            <Dialog
                open={statusChangeDialogOpen}
                onClose={() => setStatusChangeDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "11px",
                        background: "#2a2a2a",
                        border: "1px solid #474747ff",
                    },
                }}
            >
                <DialogTitle>
                    <Typography variant="h6" component="div" fontWeight={600}>
                        {t("documents.changeStatus") || "Zmień status dokumentu"}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: "text.primary", mb: 2 }}>
                        {t("documents.selectNewStatus") || "Wybierz nowy status:"}
                    </Typography>
                    <FormControl fullWidth>
                        <InputLabel sx={{ color: "text.secondary" }}>
                            {t("documents.form.status") || "Status"}
                        </InputLabel>
                        <Select
                            value={newStatus}
                            onChange={(e) => setNewStatus(e.target.value)}
                            sx={{
                                color: "text.primary",
                                backgroundColor: "#2a2a2a",
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#474747ff",
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "rgba(255,255,255,0.5)",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "primary.main",
                                },
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        backgroundColor: "#2a2a2a",
                                        border: "1px solid #474747ff",
                                        "& .MuiMenuItem-root": {
                                            color: "white",
                                            backgroundColor: "#2a2a2a",
                                            "&:hover": {
                                                backgroundColor: "rgba(255,255,255,0.1)",
                                            },
                                            "&.Mui-selected": {
                                                backgroundColor: "rgba(25, 118, 210, 0.2)",
                                                "&:hover": {
                                                    backgroundColor: "rgba(25, 118, 210, 0.3)",
                                                },
                                            },
                                        },
                                    },
                                },
                            }}
                        >
                            {(document?.allowed_transitions || []).map((statusKey) => (
                                <MenuItem key={statusKey} value={statusKey}>
                                    {STATUS_LABELS[statusKey] || statusKey}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
                    <Button
                        onClick={() => setStatusChangeDialogOpen(false)}
                        variant="outlined"
                        color="inherit"
                        sx={{ minWidth: 100 }}
                    >
                        {t("common.cancel") || "Anuluj"}
                    </Button>
                    <Button
                        onClick={handleStatusChange}
                        variant="contained"
                        color="primary"
                        sx={{ minWidth: 100 }}
                        disabled={isSaving || !newStatus || newStatus === document?.document_status}
                    >
                        {isSaving ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            t("common.confirm") || "Potwierdź"
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Create PZ from Awizacja Dialog — 2-step Stepper */}
            <Dialog
                open={pzDialogOpen}
                onClose={() => { if (!isCreatingPZ) { setPzDialogOpen(false); setPzStep(0); } }}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: "11px",
                        background: "#2a2a2a",
                        border: "1px solid #474747ff",
                    },
                }}
            >
                <DialogTitle>
                    <Typography variant="h6" component="div" fontWeight={600}>
                        {t("documents.createPZTitle") || "Utwórz dokument PZ z Awizacji"}
                    </Typography>
                    <Stepper activeStep={pzStep} sx={{ mt: 2 }}>
                        <Step>
                            <StepLabel sx={{ "& .MuiStepLabel-label": { color: "text.secondary" }, "& .Mui-active .MuiStepLabel-label": { color: "primary.main" }, "& .Mui-completed .MuiStepLabel-label": { color: "primary.main" } }}>
                                {t("documents.pz.step1", "Ilości i lokalizacje")}
                            </StepLabel>
                        </Step>
                        <Step>
                            <StepLabel sx={{ "& .MuiStepLabel-label": { color: "text.secondary" }, "& .Mui-active .MuiStepLabel-label": { color: "primary.main" }, "& .Mui-completed .MuiStepLabel-label": { color: "primary.main" } }}>
                                {t("documents.pz.barcodeStep", "Kody kreskowe")}
                            </StepLabel>
                        </Step>
                    </Stepper>
                </DialogTitle>
                <DialogContent>
                    {/* STEP 0: Quantities and Locations */}
                    {pzStep === 0 && (
                        <>
                            <Typography sx={{ color: "text.primary", mb: 2 }}>
                                {t("documents.createPZDescription") || "Wprowadź faktycznie przyjęte ilości. Jeśli różnią się od zamówionych, automatycznie zostanie utworzony dokument korekty (KOR)."}
                            </Typography>
                            <TableContainer component={Paper} sx={{ backgroundColor: "transparent" }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ color: "white", borderColor: "#474747ff" }}>
                                                {t("documents.columns.productName") || "Produkt"}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: "white", borderColor: "#474747ff" }}>
                                                {t("documents.pzDialog.orderedQty") || "Zamówiono"}
                                            </TableCell>
                                            <TableCell align="center" sx={{ color: "white", borderColor: "#474747ff" }}>
                                                {t("documents.pzDialog.receivedQty") || "Przyjęto"}
                                            </TableCell>
                                            <TableCell sx={{ color: "white", borderColor: "#474747ff" }}>
                                                {t("documents.pzDialog.storageLocation") || "Lokalizacja *"}
                                            </TableCell>
                                            <TableCell align="right" sx={{ color: "white", borderColor: "#474747ff" }}>
                                                {t("documents.pzDialog.difference") || "Różnica"}
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {document?.products?.map((product) => {
                                            const received = pzQuantities[product.product_id] ?? product.quantity;
                                            const diff = received - product.quantity;
                                            const splits = pzLocations[product.product_id] ?? [{ locationId: null, quantity: received }];
                                            const splitsQtySum = splits.reduce((sum, s) => sum + s.quantity, 0);

                                            const selectSx = {
                                                minWidth: 160,
                                                color: "text.primary",
                                                backgroundColor: "#2a2a2a",
                                                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#474747ff" },
                                                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.5)" },
                                            };
                                            const menuProps = {
                                                PaperProps: {
                                                    sx: {
                                                        backgroundColor: "#2a2a2a",
                                                        border: "1px solid #474747ff",
                                                        maxHeight: 300,
                                                        "& .MuiMenuItem-root": {
                                                            color: "white",
                                                            "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
                                                            "&.Mui-selected": { backgroundColor: "rgba(25, 118, 210, 0.2)" },
                                                        },
                                                    },
                                                },
                                            };

                                            return (
                                                <TableRow key={product.product_id} sx={{ verticalAlign: 'top' }}>
                                                    <TableCell sx={{ color: "white", borderColor: "#474747ff" }}>
                                                        <Typography variant="body2">{product.product_name}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {product.product_code}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right" sx={{ color: "white", borderColor: "#474747ff" }}>
                                                        {product.quantity}
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ borderColor: "#474747ff" }}>
                                                        <TextField
                                                            type="number"
                                                            value={received}
                                                            onChange={(e) => {
                                                                const val = parseInt(e.target.value) || 0;
                                                                const capped = Math.min(Math.max(0, val), product.quantity);
                                                                setPzQuantities((prev) => ({ ...prev, [product.product_id]: capped }));
                                                                setPzLocations((prev) => {
                                                                    const currentSplits = prev[product.product_id] ?? [{ locationId: null, quantity: capped }];
                                                                    if (currentSplits.length === 1) {
                                                                        return { ...prev, [product.product_id]: [{ ...currentSplits[0], quantity: capped }] };
                                                                    }
                                                                    return prev;
                                                                });
                                                            }}
                                                            size="small"
                                                            inputProps={{ min: 0, max: product.quantity, style: { textAlign: 'center' } }}
                                                            sx={{ width: 100, ...textFieldStyles }}
                                                            disabled={isCreatingPZ}
                                                        />
                                                        {splits.length > 1 && splitsQtySum !== received && (
                                                            <Typography variant="caption" sx={{ color: '#f44336', display: 'block', mt: 0.5 }}>
                                                                Suma: {splitsQtySum}/{received}
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell sx={{ borderColor: "#474747ff" }}>
                                                        {splits.map((split, splitIdx) => (
                                                            <Box key={splitIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: splitIdx < splits.length - 1 ? 1 : 0 }}>
                                                                {splits.length > 1 && (
                                                                    <TextField
                                                                        type="number"
                                                                        value={split.quantity}
                                                                        onChange={(e) => {
                                                                            const val = parseInt(e.target.value) || 0;
                                                                            setPzLocations((prev) => {
                                                                                const updated = [...(prev[product.product_id] ?? [])];
                                                                                updated[splitIdx] = { ...updated[splitIdx], quantity: Math.max(0, val) };
                                                                                return { ...prev, [product.product_id]: updated };
                                                                            });
                                                                        }}
                                                                        size="small"
                                                                        inputProps={{ min: 1, style: { textAlign: 'center' } }}
                                                                        sx={{ width: 70, ...textFieldStyles }}
                                                                        disabled={isCreatingPZ}
                                                                    />
                                                                )}
                                                                <FormControl size="small" sx={{ flex: 1 }}>
                                                                    <Select
                                                                        value={split.locationId ?? ""}
                                                                        onChange={(e) => {
                                                                            setPzLocations((prev) => {
                                                                                const updated = [...(prev[product.product_id] ?? [])];
                                                                                updated[splitIdx] = { ...updated[splitIdx], locationId: e.target.value as number };
                                                                                return { ...prev, [product.product_id]: updated };
                                                                            });
                                                                        }}
                                                                        displayEmpty
                                                                        sx={selectSx}
                                                                        MenuProps={menuProps}
                                                                        disabled={isCreatingPZ}
                                                                    >
                                                                        <MenuItem value="" disabled>
                                                                            <em>{t("documents.pzDialog.selectLocation") || "Wybierz lokalizację"}</em>
                                                                        </MenuItem>
                                                                        {storageLocations.map((loc) => (
                                                                            <MenuItem key={loc.id} value={loc.id}>
                                                                                {loc.rack_code
                                                                                    ? `${loc.rack_code} / ${loc.code} - ${loc.name}`
                                                                                    : `${loc.code} - ${loc.name}`}
                                                                            </MenuItem>
                                                                        ))}
                                                                    </Select>
                                                                </FormControl>
                                                                {splitIdx === 0 && (
                                                                    <Tooltip title="Skanuj etykietę lokalizacji">
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handlePzScanLocationQR(product.product_id)}
                                                                            disabled={isCreatingPZ}
                                                                            sx={{ color: '#4caf50' }}
                                                                        >
                                                                            <QrCodeScannerIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                )}
                                                                {splits.length > 1 && (
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => {
                                                                            setPzLocations((prev) => {
                                                                                const updated = [...(prev[product.product_id] ?? [])];
                                                                                updated.splice(splitIdx, 1);
                                                                                return { ...prev, [product.product_id]: updated.length ? updated : [{ locationId: null, quantity: received }] };
                                                                            });
                                                                        }}
                                                                        disabled={isCreatingPZ}
                                                                        sx={{ color: '#f44336' }}
                                                                    >
                                                                        <Typography variant="caption" sx={{ fontWeight: 700 }}>✕</Typography>
                                                                    </IconButton>
                                                                )}
                                                            </Box>
                                                        ))}
                                                        {received > 1 && (
                                                            <Button
                                                                size="small"
                                                                startIcon={<AddIcon />}
                                                                onClick={() => {
                                                                    setPzLocations((prev) => {
                                                                        const currentSplits = prev[product.product_id] ?? [{ locationId: null, quantity: received }];
                                                                        const usedQty = currentSplits.reduce((sum, s) => sum + s.quantity, 0);
                                                                        const remaining = Math.max(0, received - usedQty);
                                                                        if (currentSplits.length === 1) {
                                                                            const firstQty = Math.ceil(received / 2);
                                                                            return {
                                                                                ...prev,
                                                                                [product.product_id]: [
                                                                                    { ...currentSplits[0], quantity: firstQty },
                                                                                    { locationId: null, quantity: received - firstQty },
                                                                                ],
                                                                            };
                                                                        }
                                                                        return {
                                                                            ...prev,
                                                                            [product.product_id]: [
                                                                                ...currentSplits,
                                                                                { locationId: null, quantity: remaining || 1 },
                                                                            ],
                                                                        };
                                                                    });
                                                                }}
                                                                disabled={isCreatingPZ}
                                                                sx={{ mt: 0.5, color: '#4caf50', textTransform: 'none', fontSize: '0.75rem' }}
                                                            >
                                                                Dodaj lokalizację
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            borderColor: "#474747ff",
                                                            color: diff < 0 ? "#f44336" : diff > 0 ? "#4caf50" : "white",
                                                            fontWeight: diff !== 0 ? 600 : 400,
                                                        }}
                                                    >
                                                        {diff !== 0 ? `${diff > 0 ? '+' : ''}${diff}` : '-'}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box sx={{ mt: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AutoFixHighIcon />}
                                    disabled={isCreatingPZ}
                                    onClick={async () => {
                                        if (!document?.location || !document?.products) return;
                                        for (const product of document.products) {
                                            const qty = pzQuantities[product.product_id] ?? product.quantity;
                                            if (qty <= 0) continue;
                                            try {
                                                const result = await suggestLocations(product.product_id, document.location, qty);
                                                if (result.auto_suggest_enabled && result.suggestions.length > 0) {
                                                    setPzLocations((prev) => ({
                                                        ...prev,
                                                        [product.product_id]: result.suggestions.map((s) => ({
                                                            locationId: s.location_id,
                                                            quantity: s.suggested_quantity,
                                                        })),
                                                    }));
                                                } else if (!result.auto_suggest_enabled) {
                                                    showAlert("info", "Automatyczne sugestie lokalizacji są wyłączone dla tego magazynu. Włącz w ustawieniach magazynu.");
                                                    break;
                                                }
                                            } catch {
                                                // ignore suggestion errors
                                            }
                                        }
                                    }}
                                    sx={{ textTransform: 'none', borderColor: '#4caf50', color: '#4caf50' }}
                                >
                                    Sugeruj lokalizacje
                                </Button>
                            </Box>

                            <Alert
                                severity="info"
                                sx={{
                                    mt: 2,
                                    bgcolor: "rgba(25, 118, 210, 0.1)",
                                    color: "#fff",
                                    "& .MuiAlert-message": { color: "#fff" },
                                    "& .MuiAlert-icon": { color: "#fff" },
                                }}
                            >
                                {t("documents.pzDialog.locationOptional") || "Lokalizacja w PZ jest opcjonalna. Brak lokalizacji oznacza przyjęcie do lokalizacji TEMP."}
                            </Alert>

                            {document?.products?.some((p) => (pzQuantities[p.product_id] ?? p.quantity) < p.quantity) && (
                                <Box sx={{ mt: 2, p: 2, backgroundColor: "rgba(255, 152, 0, 0.1)", borderRadius: 1, border: "1px solid rgba(255, 152, 0, 0.3)" }}>
                                    <Typography variant="body2" sx={{ color: "#ff9800" }}>
                                        {t("documents.pzDialog.korWarning") || "Uwaga: Różnice w ilościach spowodują automatyczne utworzenie dokumentu korekty (KOR)."}
                                    </Typography>
                                </Box>
                            )}
                        </>
                    )}

                    {/* STEP 1: Barcodes per product */}
                    {pzStep === 1 && (
                        <>
                            <Typography sx={{ color: "text.primary", mb: 2 }}>
                                {t("documents.pz.barcodeStepDescription") || "Dla każdego produktu możesz zeskanować kod kreskowy, wygenerować nowy lub pominąć ten krok."}
                            </Typography>
                            <Stack spacing={1.5}>
                                {document?.products?.map((product) => {
                                    const status = pzBarcodeStatuses[product.product_id] || "pending";
                                    const barcodes = pzProductBarcodes[product.product_id] || [];
                                    const primary = barcodes.find((b) => b.is_primary) || barcodes[0];
                                    const isLoadingThis = pzBarcodeLoading === product.product_id;

                                    return (
                                        <Box
                                            key={product.product_id}
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                border: "1px solid",
                                                borderColor: status === "pending" ? "rgba(255,255,255,0.15)"
                                                    : status === "skipped" ? "rgba(255,255,255,0.08)"
                                                    : "primary.main",
                                                bgcolor: status === "pending" ? "transparent"
                                                    : status === "skipped" ? "rgba(255,255,255,0.02)"
                                                    : "rgba(0, 169, 140, 0.08)",
                                                transition: "all 0.2s",
                                            }}
                                        >
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Box sx={{ flex: 1 }}>
                                                    <Stack direction="row" alignItems="center" spacing={1}>
                                                        {status === "has_primary" || status === "scanned" || status === "generated" ? (
                                                            <CheckCircleIcon sx={{ color: "primary.main" }} fontSize="small" />
                                                        ) : status === "skipped" ? (
                                                            <SkipNextIcon sx={{ color: "text.secondary" }} fontSize="small" />
                                                        ) : (
                                                            <WarningAmberIcon sx={{ color: "warning.main" }} fontSize="small" />
                                                        )}
                                                        <Typography variant="body2" fontWeight={500}>
                                                            {product.product_name}
                                                        </Typography>
                                                    </Stack>
                                                    {primary && (
                                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 3.5, fontFamily: "monospace" }}>
                                                            {primary.code} ({primary.barcode_type_display})
                                                        </Typography>
                                                    )}
                                                </Box>

                                                <Stack direction="row" spacing={0.5}>
                                                    {(status === "has_primary" || status === "scanned" || status === "generated") && primary && (
                                                        <Tooltip title={t("barcodes.print", "Drukuj etykietę")}>
                                                            <IconButton size="small" onClick={() => handlePzPrintLabel(product.product_id)}
                                                                sx={{ color: "text.secondary" }}>
                                                                <PrintIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {(status === "pending" || status === "skipped") && (
                                                        <>
                                                            <Tooltip title={t("barcodes.scan", "Skanuj kod")}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handlePzScanBarcode(product.product_id)}
                                                                    disabled={isLoadingThis}
                                                                    sx={{ color: "primary.main" }}
                                                                >
                                                                    <QrCodeScannerIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title={t("barcodes.generate", "Generuj kod")}>
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handlePzGenerateBarcode(product.product_id)}
                                                                    disabled={isLoadingThis}
                                                                    sx={{ color: "text.secondary" }}
                                                                >
                                                                    {isLoadingThis ? <CircularProgress size={16} /> : <AutoFixHighIcon fontSize="small" />}
                                                                </IconButton>
                                                            </Tooltip>
                                                            {status === "pending" && (
                                                                <Tooltip title={t("barcodes.skip", "Pomiń")}>
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handlePzSkipBarcode(product.product_id)}
                                                                        sx={{ color: "text.secondary" }}
                                                                    >
                                                                        <SkipNextIcon fontSize="small" />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </>
                                                    )}
                                                </Stack>
                                            </Stack>
                                        </Box>
                                    );
                                })}
                            </Stack>

                            <BarcodeScanner
                                open={pzScannerOpen}
                                onScan={handlePzScanResult}
                                onClose={() => { setPzScannerOpen(false); setPzScanningForProductId(null); }}
                                title={t("barcodes.scanForProduct", "Skanuj kod kreskowy produktu")}
                            />

                            <BarcodeScanner
                                open={pzLocationScannerOpen}
                                onScan={handlePzLocationScanResult}
                                onClose={() => { setPzLocationScannerOpen(false); setPzLocationScanProductId(null); }}
                                title="Skanuj QR etykiety lokalizacji"
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0, gap: 1 }}>
                    <Button
                        onClick={() => { if (pzStep === 0) { setPzDialogOpen(false); } else { handlePzBackStep(); } }}
                        variant="outlined"
                        color="inherit"
                        sx={{ minWidth: 100 }}
                        disabled={isCreatingPZ}
                    >
                        {pzStep === 0 ? (t("common.cancel") || "Anuluj") : (t("common.back") || "Wstecz")}
                    </Button>
                    {pzStep === 0 ? (
                        <Button
                            onClick={handlePzNextStep}
                            variant="contained"
                            color="primary"
                            sx={{ minWidth: 160 }}
                        >
                            {t("common.next") || "Dalej"}
                        </Button>
                    ) : (
                        <Button
                            onClick={handleCreatePZ}
                            variant="contained"
                            color="success"
                            sx={{ minWidth: 160 }}
                            disabled={isCreatingPZ || !pzBarcodeStepComplete}
                        >
                            {isCreatingPZ ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : (
                                t("documents.createPZ") || "Utwórz PZ"
                            )}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default DocumentDetails;
